import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Shell from '../../components/Shell';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import type { Contact, ContactPhase } from './Crm';

type FundOption = {
  slug: string;
  name: string;
  pillar: string;
};

type EmailRow = {
  _id: string;
  direction: 'outbound' | 'inbound';
  from: string;
  to: string;
  subject: string;
  text: string;
  html?: string;
  messageId: string;
  threadId: string;
  createdAt?: string;
};

const PHASES: { id: ContactPhase; label: string }[] = [
  { id: 'to_contact', label: 'To contact' },
  { id: 'reached_out', label: 'Reached out' },
  { id: 'invited', label: 'Invited' },
  { id: 'in_conversation', label: 'In conversation' },
  { id: 'soft_interest', label: 'Soft interest' },
  { id: 'committed', label: 'Committed' },
  { id: 'passed', label: 'Passed' },
];

export default function AdminContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contact, setContact] = useState<Contact | null>(null);
  const [funds, setFunds] = useState<FundOption[]>([]);
  const [emails, setEmails] = useState<EmailRow[]>([]);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const [c, f, e] = await Promise.all([
      api.get(`/admin/crm/contacts/${id}`),
      api.get('/admin/crm/funds'),
      api.get(`/admin/crm/contacts/${id}/emails`),
    ]);
    const liveSlugs = new Set(
      (f.data as FundOption[]).map((fund) => fund.slug),
    );
    const contactData = c.data as Contact;
    setContact({
      ...contactData,
      visibleFundSlugs: (contactData.visibleFundSlugs || []).filter((s) =>
        liveSlugs.has(s),
      ),
    });
    setFunds(f.data);
    setEmails(e.data);
  }, [id]);

  useEffect(() => {
    load().catch(() => setMsg('Failed to load contact'));
  }, [load]);

  async function saveContact(patch: Partial<Contact>) {
    if (!id || !contact) return;
    setSaving(true);
    setMsg('');
    try {
      const { data } = await api.patch(`/admin/crm/contacts/${id}`, patch);
      setContact(data);
      setMsg('Saved.');
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function toggleFund(slug: string) {
    if (!contact) return;
    const current = contact.visibleFundSlugs || [];
    const next = current.includes(slug)
      ? current.filter((s) => s !== slug)
      : [...current, slug];
    setContact({ ...contact, visibleFundSlugs: next });
  }

  function toggleRestructuring() {
    if (!contact) return;
    const current = contact.entitlements || [];
    const has = current.includes('restructuring');
    const next = has
      ? current.filter((e) => e !== 'restructuring')
      : [...current, 'restructuring'];
    setContact({ ...contact, entitlements: next });
  }

  async function onSend(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setMsg('');
    try {
      await api.post(`/admin/crm/contacts/${id}/emails`, {
        subject,
        text: body,
        inReplyTo: replyToId || undefined,
      });
      setSubject('');
      setBody('');
      setReplyToId(null);
      setMsg('Email sent.');
      await load();
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'Send failed');
    }
  }

  async function invite() {
    if (!id) return;
    if (
      !window.confirm(
        'Create portal access for this contact and send an invite email?',
      )
    ) {
      return;
    }
    try {
      await api.post(`/admin/crm/contacts/${id}/invite`, { sendInvite: true });
      setTempPassword(null);
      setMsg('Portal invite sent.');
      await load();
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'Invite failed');
    }
  }

  async function resetPassword() {
    if (!id) return;
    if (
      !window.confirm(
        'Reset this person’s portal password and email them a temporary password?',
      )
    ) {
      return;
    }
    setResettingPassword(true);
    setMsg('');
    setTempPassword(null);
    try {
      const { data } = await api.post(
        `/admin/crm/contacts/${id}/reset-password`,
        { sendEmail: true },
      );
      setTempPassword(data.temporaryPassword || null);
      if (data.emailed) {
        setMsg('Password reset. Temporary password emailed to the user.');
      } else if (data.emailError) {
        setMsg(
          `Password reset, but email failed: ${data.emailError}. Share the temporary password below.`,
        );
      } else {
        setMsg('Password reset. Share the temporary password below.');
      }
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setMsg(
        Array.isArray(message)
          ? message.join(', ')
          : message || 'Password reset failed',
      );
    } finally {
      setResettingPassword(false);
    }
  }

  async function remove() {
    if (!id) return;
    if (!window.confirm('Delete this contact and their email history?')) return;
    await api.delete(`/admin/crm/contacts/${id}`);
    navigate('/admin/crm');
  }

  function startReply(email: EmailRow) {
    setReplyToId(email.messageId);
    const bare = email.subject.replace(/^(re|fwd):\s*/i, '');
    setSubject(email.subject.match(/^re:/i) ? email.subject : `Re: ${bare}`);
  }

  if (!contact) {
    return (
      <Shell title="Admin · Contact">
        <p className="text-ink-700">{msg || 'Loading…'}</p>
      </Shell>
    );
  }

  return (
    <Shell title="Admin · Contact" wide>
      <Link to="/admin/crm" className="text-sm text-ink-700 underline">
        ← Pipeline
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">
            {contact.firstName} {contact.lastName}
          </h1>
          <p className="mt-1 text-sm text-ink-700">{contact.email}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {contact.userId && (
            <button
              type="button"
              disabled={resettingPassword}
              onClick={resetPassword}
              className="border border-navy-900 px-4 py-2 text-sm font-semibold text-navy-900 hover:bg-navy-900 hover:text-mist-50 disabled:opacity-50"
            >
              {resettingPassword ? 'Resetting…' : 'Reset password'}
            </button>
          )}
          <button
            type="button"
            onClick={invite}
            className="bg-navy-900 px-4 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700"
          >
            {contact.userId ? 'Re-invite to portal' : 'Invite to portal'}
          </button>
          <button
            type="button"
            onClick={remove}
            className="text-sm text-ink-700 underline"
          >
            Delete
          </button>
        </div>
      </div>

      {msg && <p className="mt-3 text-sm text-ink-700">{msg}</p>}
      {tempPassword && (
        <div className="mt-3 border border-navy-200 bg-navy-900/[0.04] px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-600">
            Temporary password
          </p>
          <p className="mt-1 font-mono text-sm text-ink-900">{tempPassword}</p>
          <p className="mt-1 text-xs text-ink-700">
            Copy this now. It will not be shown again after you leave this page.
          </p>
        </div>
      )}

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <section className="space-y-6">
          <div className="border border-mist-200 bg-white p-5">
            <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-600">
              Profile
            </h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input
                value={contact.firstName}
                onChange={(e) =>
                  setContact({ ...contact, firstName: e.target.value })
                }
                placeholder="First name"
              />
              <input
                value={contact.lastName}
                onChange={(e) =>
                  setContact({ ...contact, lastName: e.target.value })
                }
                placeholder="Last name"
              />
              <input
                type="email"
                value={contact.email}
                onChange={(e) =>
                  setContact({ ...contact, email: e.target.value })
                }
                placeholder="Email"
              />
              <input
                value={contact.phone || ''}
                onChange={(e) =>
                  setContact({ ...contact, phone: e.target.value })
                }
                placeholder="Phone"
              />
              <input
                className="sm:col-span-2"
                value={contact.company || ''}
                onChange={(e) =>
                  setContact({ ...contact, company: e.target.value })
                }
                placeholder="Company"
              />
              <textarea
                className="sm:col-span-2 min-h-[90px]"
                value={contact.notes || ''}
                onChange={(e) =>
                  setContact({ ...contact, notes: e.target.value })
                }
                placeholder="Notes"
              />
              <label className="sm:col-span-2 text-sm text-ink-700">
                Phase
                <select
                  className="mt-1 block w-full"
                  value={contact.phase}
                  onChange={(e) =>
                    setContact({
                      ...contact,
                      phase: e.target.value as ContactPhase,
                    })
                  }
                >
                  {PHASES.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <button
              type="button"
              disabled={saving}
              onClick={() =>
                saveContact({
                  firstName: contact.firstName,
                  lastName: contact.lastName,
                  email: contact.email,
                  phone: contact.phone,
                  company: contact.company,
                  notes: contact.notes,
                  phase: contact.phase,
                })
              }
              className="mt-4 bg-navy-900 px-4 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700 disabled:opacity-50"
            >
              Save profile
            </button>
          </div>

          <div className="border border-mist-200 bg-white p-5">
            <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-600">
              Portal visibility
            </h2>
            <p className="mt-2 text-sm text-ink-700">
              Choose what this person can see after they have portal access.
            </p>
            <ul className="mt-4 space-y-2">
              {funds.map((f) => {
                const checked = (contact.visibleFundSlugs || []).includes(
                  f.slug,
                );
                return (
                  <li key={f.slug}>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={checked}
                        onChange={() => toggleFund(f.slug)}
                      />
                      <span>
                        <span className="font-medium">{f.name}</span>
                        <span className="text-ink-700"> · {f.pillar}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={(contact.entitlements || []).includes('restructuring')}
                onChange={toggleRestructuring}
              />
              Restructuring page
            </label>
            <button
              type="button"
              disabled={saving}
              onClick={() =>
                saveContact({
                  visibleFundSlugs: contact.visibleFundSlugs || [],
                  entitlements: contact.entitlements || [],
                })
              }
              className="mt-4 bg-navy-900 px-4 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700 disabled:opacity-50"
            >
              Save visibility
            </button>
          </div>
        </section>

        <section className="border border-mist-200 bg-white p-5">
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-600">
            Email thread
          </h2>
          <p className="mt-2 text-xs text-ink-700">
            Sent as {user?.email || 'your admin account'}. Replies go to
            reply.consularcapital.com (SendGrid Inbound Parse) and show up here.
          </p>

          <div className="mt-4 max-h-[28rem] space-y-3 overflow-y-auto">
            {emails.length === 0 && (
              <p className="text-sm text-ink-700">No messages yet.</p>
            )}
            {emails.map((em) => (
              <article
                key={em._id}
                className={`border px-3 py-3 ${
                  em.direction === 'outbound'
                    ? 'border-navy-200 bg-navy-900/[0.03]'
                    : 'border-mist-200 bg-mist-50'
                }`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-600">
                    {em.direction === 'outbound' ? 'You' : 'Them'} ·{' '}
                    {em.subject}
                  </p>
                  {em.createdAt && (
                    <time className="text-[11px] text-ink-700/70">
                      {new Date(em.createdAt).toLocaleString()}
                    </time>
                  )}
                </div>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-ink-900">
                  {em.text || '(empty body)'}
                </pre>
                <button
                  type="button"
                  onClick={() => startReply(em)}
                  className="mt-2 text-xs underline text-ink-700"
                >
                  Reply
                </button>
              </article>
            ))}
          </div>

          <form
            onSubmit={onSend}
            className="mt-6 flex flex-col gap-3 border-t border-mist-200 pt-4"
          >
            {replyToId && (
              <p className="text-xs text-ink-700">
                Replying in thread{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => setReplyToId(null)}
                >
                  (cancel reply)
                </button>
              </p>
            )}
            <input
              required
              className="block w-full"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <textarea
              required
              className="block w-full min-h-[140px] resize-y"
              placeholder="Message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <button
              type="submit"
              className="self-start bg-navy-900 px-4 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700"
            >
              Send email
            </button>
          </form>
        </section>
      </div>
    </Shell>
  );
}
